import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom, map, Subject, timeout } from 'rxjs';
import { SectionViewTracker } from 'src/app/models/views/section-view-tracker';

import { DeviceDetectorService } from 'ngx-device-detector';
import { HttpClient } from '@angular/common/http';
import { SiteVisitTracker } from 'src/app/models/views/sitevisit-tracker';
import { PageViewTracker } from 'src/app/models/views/pageview-tracker';
import { StatisticsService } from '../statistics.service';
import { BasePayload, OpenViewBasePayload, OpenViewPayloadMap, Payload, PublicTrackerMap, StartContentViewPayload, TrackerMap, ViewTrackerType } from 'src/app/models/payloads/view-payloads';
import { SiteVisitCookieService } from './site-visit-cookie.service';

@Injectable({
    providedIn: 'root',
})
export class ViewManagerService {
    private basePayloadBody!: BasePayload;
    private siteVisitView: SiteVisitTracker | null = null;
    private pageView: PageViewTracker;
    private sectionView: SectionViewTracker;
    private pingerSubject: Subject<void> = new Subject();
    private navigationChangedSubject: Subject<string> = new Subject();
    private readonly pinger$ = this.pingerSubject.asObservable();
    private readonly navigationChange$ = this.navigationChangedSubject.asObservable();
    private trackerMap!: TrackerMap;
    private publicTrackerMap!: PublicTrackerMap;

    private userAgent!: string;
    private sourceId!: number;
    private device!: string;
    private deviceId: number = 4;
    private ip: string = 'unknown';
    private country!: string;
    private city!: string
    
    public hasAdBlock!: boolean;
    private scrollerSubject = new Subject<void>();
    private readonly scrollReachedBottom$ = this.scrollerSubject.asObservable();

    constructor(private statApi: StatisticsService, private router: Router, private deviceService: DeviceDetectorService, 
    private siteVisitCookie: SiteVisitCookieService,
        private http: HttpClient) {
        // this.siteVisitView = new SiteVisitTracker(this.statApi);
        this.pageView = new PageViewTracker(this.statApi);
        this.sectionView = new SectionViewTracker(this.statApi);
        this.trackerMap = {
            [ViewTrackerType.Site]: this.siteVisitView,
            [ViewTrackerType.Page]: this.pageView,
            [ViewTrackerType.Section]: this.sectionView,
        };
        this.publicTrackerMap = this.trackerMap;
    }

    // public async loadViewManager(siteVisitId: string | null, siteVisitTimestamp: string): Promise<void> {
    //     console.log("Carga View Manager......");
    //     if (!!this.siteVisitView) return;
    //     this.initVariables().catch(() => {/* swallow */});
    //     await this.setupSiteVisit(siteVisitId, siteVisitTimestamp);
    //     await this.initSectionView();
    //     await this.init();
    // }

    public async loadViewManager(): Promise<string | null> {
        if (!!this.siteVisitView) return this.siteVisitView.getTimeStamp();

        await this.initVariables();

        await this.verifySiteVisit();

        this.setTrackerMaps();
        await this.initSectionView();
        await this.init();

        return this.siteVisitView!.getTimeStamp()
    }

    private async init(): Promise<void> {
        this.listenToNavigationChanges();
        this.listenToPinger();
    }

    // ------------- Set sitevisit if exists. If not, create new one ----------------

    // private async setupSiteVisit(siteVisitId: string | null, siteVisitTimestamp: string): Promise<void> {
    //     console.log("Setea sitevisit...");
    //     this.statApi.siteVisitUuid = siteVisitId;
    //     const siteVisitPayload: Payload = this.getPayloadData();
    //     this.siteVisitView = new SiteVisitTracker(this.statApi, siteVisitId, siteVisitTimestamp);
    //     const baseViewPayload: OpenViewBasePayload = { ...siteVisitPayload, site_visit_id: this.siteVisitView.getViewId() }
    //     this.setBasePayload(baseViewPayload);
    // }

    // ------------- Create new section view ----------------

    private async initSectionView(): Promise<void> {
        await this.openView(ViewTrackerType.Section, {sectionId: 15});
    }

    // ------------- Listen for route change ----------------

    /**
     * Because the views are dependant of the currentUrl, this method listens for route change events emitted by the activity manager on navigationEnd
     * and scans the currentUrl to determine which view to create which is done in the handleViewChanges() method.
     */
    private listenToNavigationChanges(): void {
        this.navigationChange$.subscribe((urlAfterRedirects: string) => {
            this.handleViewChanges();
        });
    }

    // ------------- Open & close views based on route change ----------------

    private async handleViewChanges() {
        let openingTasks: Promise<void>[] = [];
        let closingTasks: Promise<void>[] = [];
        let startingTasks: Promise<void>[] = [];

        // CIERRE DE VIEWS

        closingTasks.push(this.closeView(ViewTrackerType.Page)); // PAGE VIEW

        await Promise.all(closingTasks);

        // APERTURA DE VIEWS

        openingTasks.push(this.openView(ViewTrackerType.Page)); // PAGE VIEW

        await Promise.all(openingTasks);

        // START DE VIEWS 

        startingTasks.push(this.startView(ViewTrackerType.Page)); // PAGE VIEW

        await Promise.all(startingTasks);

        // FINISH DE VIEWS 

        // await Promise.all(finishingTasks);

    }

    private getTracker<T extends ViewTrackerType>(type: T): TrackerMap[T] {
        return this.trackerMap[type]!;
    }

    private getPublicTracker<T extends ViewTrackerType>(type: T): PublicTrackerMap[T] {
        return this.publicTrackerMap[type]!;
    }

    // ------------- Close view methods ----------------

    private async closeView(viewType: ViewTrackerType): Promise<void> {
        const tracker = this.getTracker(viewType);
        return tracker!.close(tracker!.getViewId());
    }


    // ------------- Open view methods ----------------

    private async openView(viewType: ViewTrackerType, id?: any): Promise<void> {
        const tracker = this.getTracker(viewType);
        const payload = this.getOpenPayload(viewType, id);
        return tracker!.open(payload, id);
    }

    // ------------- Start view methods ----------------

    private async startView(viewType: ViewTrackerType, payload?: StartContentViewPayload): Promise<void> {
        const tracker = this.getTracker(viewType);
        return tracker!.start(tracker!.getViewId(), payload);
    }

    // ------------- Finish view methods ----------------

    private async finishView(viewType: ViewTrackerType, payload?: any): Promise<void> {
        const tracker = this.getTracker(viewType);
        return tracker!.finish(tracker!.getViewId(), payload);
    }

    // ------------- Listen to ping opened views ----------------

    private listenToPinger() {
        this.pinger$.subscribe(() => {
            this.triggerPings();
        });
    }

    private async triggerPings(): Promise<void> {
        this.siteVisitView!.triggerPing(this.siteVisitView!.getViewId()); // SITEVISIT
        this.pageView.triggerPing(this.pageView.getViewId()); // PAGE VIEW
        this.sectionView.triggerPing(this.sectionView.getViewId()); // SECTION VIEW
    }

    private getOpenPayload<T extends ViewTrackerType>(viewType: T, id?: string): any {
        const base = this.getBasePayload();

        switch (viewType) {
            case ViewTrackerType.Page:
                const url = this.router.url.slice(0, this.router.url.indexOf('?'));
                return { ...base, name: url, url: url } ;

            case ViewTrackerType.Section:
                return { ...base, section_id: id };

            default:
                throw new Error("View type no manejado");
        }
    }

    // ------------- Getters & Setters ----------------

    setBasePayload(payload: BasePayload) {
        this.basePayloadBody = payload;
    }

    public getBasePayload() {
        return this.basePayloadBody;
    }

    public getPayloadData(): Payload {
        const data = {
            device: this.device,
            device_type_id: this.deviceId,
            ip: this.ip,
            location: this.city,
            country: this.country,
            user_agent: this.userAgent,
            source_id: this.sourceId
        };
        return data;
    }

    // ------------- Initialize the required variables for views ----------------

    private async initVariables(): Promise<void> {
        const cached = sessionStorage.getItem('geo_v1');
        if (cached) {
            const { ts, data } = JSON.parse(cached);
            if (Date.now() - ts < 24 * 60 * 60 * 1000) {  // 24h
                this.applyGeo(data);
                return;
            }
        }

        // Marca de breaker para evitar tormenta de solicitudes
        const breaker = sessionStorage.getItem('geo_breaker_until');
        if (breaker && Date.now() < +breaker) {
            this.applyGeo(null); // usa unknown, pero no falles
            return;
        }

        try {
            const userData: any = await lastValueFrom(
                this.http.get("https://ipapi.co/json/", { observe: 'response' }).pipe(
                    // evita que cuelgue
                    timeout({ each: 2000 }),
                    // 1 reintento suave (NO si ya es 429)
                    // map res->body abajo
                )
            );
            const data = (userData as any).body ?? userData;
            this.applyGeo(data);
            sessionStorage.setItem('geo_v1', JSON.stringify({ ts: Date.now(), data }));
        } catch (e: any) {
            // Si vino 429 con Retry-After, respétalo
            const retryAfterSec = Number(e?.error?.headers?.get?.('Retry-After')) || 0;
            if (retryAfterSec > 0) {
                sessionStorage.setItem('geo_breaker_until', String(Date.now() + retryAfterSec * 1000));
            } else {
                // aplica un enfriamiento corto
                sessionStorage.setItem('geo_breaker_until', String(Date.now() + 10 * 60 * 1000));
            }
            this.applyGeo(null);
        }
    }

    private applyGeo(userData: any | null) {
        this.ip = userData?.ip ?? "unknown";
        this.country = userData?.region ?? "unknown";
        this.city = userData?.city ?? "unknown";
        const info = this.deviceService.getDeviceInfo();
        this.userAgent = info.userAgent;
        this.device = info.device;
        this.deviceId = info.deviceType === 'desktop' ? 1 : info.deviceType === 'tablet' ? 3 : 2;
        this.sourceId = 2;
    }

    // ------------- Listen to activity ----------------

    public emitNavigationChanged(url: string): void {
        this.navigationChangedSubject.next(url);
    }

    public emitPing(): void {
        this.pingerSubject.next();
    }

    // ------------- Public methods ----------------

    public exposePublicTracker<T extends ViewTrackerType>(type: T): PublicTrackerMap[T] {
        return this.getPublicTracker(type);
    }

    public closeAllViewsAsync() {
        // CIERRE DE VIEWS

        this.closeView(ViewTrackerType.Page); // PAGE VIEW
        this.closeView(ViewTrackerType.Section); // PAGE VIEW

    }

    public async closeAllViewsSync() {
        let closingTasks: Promise<void>[] = [];
        // CIERRE DE VIEWS

        closingTasks.push(this.closeView(ViewTrackerType.Page)); // PAGE VIEW
        closingTasks.push(this.closeView(ViewTrackerType.Section)); // PAGE VIEW

        await Promise.all(closingTasks);
    }

    private setTrackerMaps(): void {
        this.trackerMap = {
        [ViewTrackerType.Site]: this.siteVisitView,
        [ViewTrackerType.Page]: this.pageView,
        [ViewTrackerType.Section]: this.sectionView,
        };
    }

    // ---------- SITEVISIT ----------

    public async verifySiteVisit(): Promise<void> {
        const validSiteVisit = this.isValidSiteVisit();
        await this.setupSiteVisit(validSiteVisit);
    }

    private isValidSiteVisit(): boolean {
        return this.siteVisitCookie.isValid();
    }

    private async setupSiteVisit(validSiteVisit: boolean): Promise<void> {
        if (validSiteVisit) {
        const { uuid, timestamp } = this.siteVisitCookie.getSiteVisit();

        if (uuid && timestamp) {
            this.siteVisitView = new SiteVisitTracker(
            this.statApi,
            uuid,
            timestamp
            );
        } else {
            // if (!!this.siteVisitView) await this.closeView(ViewTrackerType.Site);
            await this.createNewSiteVisit();
        }

        } else {
        // if (!!this.siteVisitView) await this.closeView(ViewTrackerType.Site);
        await this.createNewSiteVisit();
        }

        this.setBasePayload({ siteVisitUuid: this.siteVisitView!.getViewUuid()! });
    }

    private async createNewSiteVisit(): Promise<void> {
        this.siteVisitView = new SiteVisitTracker(this.statApi);

        await this.siteVisitView.open({
        applicationId: 1,
        });

        const siteVisitUuid = this.siteVisitView.getViewUuid();
        const siteVisitTimestamp = this.siteVisitView.getTimeStamp();

        if (!siteVisitUuid || !siteVisitTimestamp) return;

        this.clearSiteVisit();
        this.siteVisitCookie.setSiteVisit(siteVisitUuid, siteVisitTimestamp);
    }

    clearSiteVisit(): void {
        this.siteVisitCookie.clear();
    }

    public emitScroll(): void {
        this.scrollerSubject.next();
    }

    public refreshSiteVisitTimestamp(timestamp: string): void {
        const siteVisitUuid = this.siteVisitView?.getViewUuid();

        if (!siteVisitUuid) return;

        this.siteVisitCookie.setSiteVisit(siteVisitUuid, timestamp);
    }

    public async ensureValidSiteVisit(reopenCurrentPage: boolean = false): Promise<string | null> {
        if (this.siteVisitCookie.isValid()) {
        return null;
        }

        await this.closeCurrentlyOpenedViewsSafely();

        await this.createNewSiteVisit();

        this.setBasePayload({
        siteVisitUuid: this.siteVisitView!.getViewUuid()!
        });

        this.setTrackerMaps();

        this.resetPreviousViewState();

        // if (reopenCurrentPage) {
        // await this.processNavigation(this.router.url);
        // }

        return this.siteVisitView!.getTimeStamp();
    }

    private async closeCurrentlyOpenedViewsSafely(): Promise<void> {
        const viewsToClose: ViewTrackerType[] = [
        ViewTrackerType.Section,
        ViewTrackerType.Page,
        ];

        for (const viewType of viewsToClose) {
        try {
            const tracker = this.getTracker(viewType);

            if (tracker?.isActive?.()) {
            await this.closeView(viewType).catch(() => undefined);
            }
        } catch {
            // No rompemos el flujo si algún tracker no aplica en cierto contexto.
        }
        }
    }

    private resetPreviousViewState(): void {
        // this.previousUrl = '';
        // this.previousSection = null;
        // this.previousCategory = null;
        // this.previousSubCategory = null;
        // this.previousContentId = -1;
        // this.previousContent = '';
    }
}